/* eslint-disable complexity */
import type { DomainError } from '@core/domain';
import { Card, Delete, Edit, Eye, IconButton, ButtonType, OverlayMode, IconSize, Shimmer } from '@soteria/design-system-ve';
import type { ReactNode } from 'react';
import { useState } from 'react';

import * as styles from './DataList.css';
import { DeleteDialog } from './DeleteDialog';
import { Illustration, IllustrationType, IllustrationLayout } from './Illustration';

export interface DeleteItemContext<T> {
  handler: (item: T) => Promise<boolean>;
  isDeleting: boolean;
  errors: DomainError[] | null;
  resetErrors: () => void;
  getItemDisplayName?: (item: T) => string;
}

export interface TableColumn<T> {
  key: string;
  header: string;
  render: (item: T, index?: number) => ReactNode;
  width?: string;
}

export interface ActionConfig<T> {
  icon: React.ComponentType;
  label: string;
  onClick: (item: T) => void;
  buttonType: ButtonType | ((item: T) => ButtonType);
  disabled?: (item: T) => boolean;
  tooltip?: (item: T) => string | undefined;
}

export interface DataListProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  onView?: (item: T) => void;
  onEdit?: (item: T) => void;
  additionalAction?: ActionConfig<T>;
  isLoading?: boolean;
  dataTypeName: string; // e.g., "action", "topic", "user" - used for delete confirmation
  variant?: 'default' | 'condensed'; // Add variant prop for condensed styling
  hideEmptyState?: boolean; // Hide the "No data" illustration when data is empty
  loadingRowCount?: number; // Number of shimmer rows to show when loading (default: 4)
  emptyStateMessage?: string; // Custom empty state message

  // Enhanced state management props
  error?: Error | string | null | unknown | Array<{ correlationId?: string }>;

  // Delete item context
  deleteItemContext?: DeleteItemContext<T>;
}

/**
 * Helper function to safely get a display name from an item
 */
function getItemDisplayName<T>(item: T): string {
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>;

    return (obj.name as string) || (obj.topicName as string) || (obj.groupName as string) || (obj.providerUrl as string) || '';
  }

  return '';
}

/**
 * Helper function to safely get a unique key from an item
 */
function getItemKey<T>(item: T, index: number): string {
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>;

    // Prioritize unique identifiers to avoid duplicate keys
    return (obj.id as string) || (obj.uuid as string) || (obj._id as string) || `item-${index}`;
  }

  return `item-${index}`;
}

/**
 * Reusable data list component for displaying lists with consistent styling
 * Used by TopicList, ActionList, and TopicActionsList
 */

export function DataList<T>({
  data,
  columns,
  onView,
  onEdit,
  additionalAction,
  isLoading = false,
  dataTypeName,
  variant = 'default',
  hideEmptyState = false,
  loadingRowCount = 4,
  emptyStateMessage,
  error,
  deleteItemContext,
}: DataListProps<T>) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);

  // Handle delete button click
  const handleDeleteClick = (item: T) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (deleteItemContext?.resetErrors) {
      deleteItemContext.resetErrors();
    }

    if (itemToDelete && deleteItemContext) {
      // Call the handler and get the result
      const success = await deleteItemContext.handler(itemToDelete);

      // Only close the dialog if deletion was successful
      if (success) {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      }
    }
  };

  // Handle delete dialog close
  const handleDeleteClose = () => {
    // Reset state when dialog is closed
    setDeleteDialogOpen(false);
    setItemToDelete(null);

    // Reset any errors when dialog is closed
    if (deleteItemContext?.resetErrors) {
      deleteItemContext.resetErrors();
    }
  };

  const renderLoadingState = () => {
    const rowIds = Array.from({ length: loadingRowCount }, (_, i) => `row-${i + 1}`);
    const isCondensed = variant === 'condensed';

    return (
      <div className={styles.loadingContainer}>
        <div className={isCondensed ? styles.loadingHeaderCondensed : styles.loadingHeader}>
          <Shimmer style={{ width: '100%', height: isCondensed ? '12px' : '16px' }} />
        </div>
        {rowIds.map((rowId, index) => (
          <div
            key={rowId}
            className={index !== rowIds.length - 1 ? (isCondensed ? styles.loadingRowWithBorderCondensed : styles.loadingRowWithBorder) : styles.loadingRow}
          >
            <div className={isCondensed ? styles.loadingContentCellCondensed : styles.loadingContentCell}>
              <Shimmer style={{ width: '100%', height: isCondensed ? '20px' : '30px' }} />
            </div>
            {showActions && (
              <div className={isCondensed ? styles.loadingActionsCellCondensed : styles.loadingActionsCell}>
                <div className={styles.loadingActionsContainer}>
                  <Shimmer style={{ width: '60px', height: isCondensed ? '20px' : '30px' }} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render error state with consistent defaults
  const renderErrorState = () => {
    let extractedCorrelationId = '';

    if (Array.isArray(error) && error.length > 0) {
      extractedCorrelationId = error[0].correlationId;
    }

    // Extract correlation ID from error if it's a single domain error
    if (!extractedCorrelationId && error && typeof error === 'object' && 'correlationId' in error) {
      extractedCorrelationId = error.correlationId as string;
    }

    const errorDescription = extractedCorrelationId ? `Please try again later. Correlation ID: ${extractedCorrelationId}` : 'Please try again later.';

    return <Illustration description={errorDescription} layout={IllustrationLayout.HORIZONTAL} title="Error loading data" type={IllustrationType.ERROR} />;
  };

  // Render empty state with consistent defaults (always horizontal, always in same card)
  const renderEmptyState = () => {
    if (variant === 'condensed') {
      return <div className={styles.emptyStateCondensed}>{emptyStateMessage || 'No data available.'}</div>;
    }

    return (
      <Illustration description="There's nothing to show here yet." layout={IllustrationLayout.HORIZONTAL} title="No data" type={IllustrationType.EMPTY} />
    );
  };

  // Render additional action button
  const renderAdditionalActionButton = (item: T) => {
    if (!additionalAction) return null;

    const buttonType = typeof additionalAction.buttonType === 'function' ? additionalAction.buttonType(item) : additionalAction.buttonType;

    return (
      <IconButton
        aria-label={`${additionalAction.label} ${getItemDisplayName(item) || dataTypeName}`}
        buttonType={buttonType}
        disabled={additionalAction.disabled ? additionalAction.disabled(item) : false}
        icon={additionalAction.icon}
        iconSize={IconSize.XSmall}
        overlayMode={OverlayMode.Solid}
        title={additionalAction.tooltip ? additionalAction.tooltip(item) : undefined}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation(); // Prevent event bubbling
          additionalAction.onClick(item);
        }}
      />
    );
  };

  // Render view button
  const renderViewButton = (item: T) => {
    if (!onView) return null;

    return (
      <IconButton
        aria-label={`View ${getItemDisplayName(item) || dataTypeName}`}
        buttonType={ButtonType.Secondary}
        icon={Eye}
        iconSize={IconSize.XSmall}
        overlayMode={OverlayMode.Solid}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation(); // Prevent event bubbling
          onView(item);
        }}
      />
    );
  };

  // Render edit button
  const renderEditButton = (item: T) => {
    if (!onEdit) return null;

    return (
      <IconButton
        aria-label={`Edit ${getItemDisplayName(item) || dataTypeName}`}
        buttonType={ButtonType.Primary}
        icon={Edit}
        iconSize={IconSize.XSmall}
        overlayMode={OverlayMode.Solid}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation(); // Prevent event bubbling
          onEdit(item);
        }}
      />
    );
  };

  // Render delete button
  const renderDeleteButton = (item: T) => {
    if (!deleteItemContext) return null;

    return (
      <IconButton
        aria-label={`Delete ${getItemDisplayName(item) || dataTypeName}`}
        buttonType={ButtonType.Danger}
        icon={Delete}
        iconSize={IconSize.XSmall}
        overlayMode={OverlayMode.Solid}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation(); // Prevent event bubbling
          handleDeleteClick(item);
        }}
      />
    );
  };

  // Determine if we should show actions column
  const showActions = !!(onView || onEdit || deleteItemContext || additionalAction);

  return (
    <Card className={variant === 'condensed' ? styles.cardCondensed : undefined}>
      {error ? (
        renderErrorState()
      ) : isLoading ? (
        renderLoadingState()
      ) : data.length === 0 && !hideEmptyState ? (
        renderEmptyState()
      ) : data.length > 0 ? (
        <div className={styles.tableContainer}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {columns.map(column => (
                  <th key={column.key} className={variant === 'condensed' ? styles.tableHeaderCondensed : styles.tableHeader} style={{ width: column.width }}>
                    {column.header}
                  </th>
                ))}
                {showActions && <th className={variant === 'condensed' ? styles.actionsHeaderCondensed : styles.actionsHeader} />}
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={getItemKey(item, index)}>
                  {columns.map(column => (
                    <td
                      key={column.key}
                      className={
                        variant === 'condensed'
                          ? index === data.length - 1
                            ? styles.tableCellCondensed
                            : styles.tableCellWithBorderCondensed
                          : index === data.length - 1
                            ? styles.tableCell
                            : styles.tableCellWithBorder
                      }
                    >
                      {column.render(item)}
                    </td>
                  ))}
                  {showActions && (
                    <td
                      className={
                        variant === 'condensed'
                          ? index === data.length - 1
                            ? styles.actionsCellCondensed
                            : styles.actionsCellWithBorderCondensed
                          : index === data.length - 1
                            ? styles.actionsCell
                            : styles.actionsCellWithBorder
                      }
                    >
                      <div className={styles.actionsContainer}>
                        {renderAdditionalActionButton(item)}
                        {renderViewButton(item)}
                        {renderEditButton(item)}
                        {renderDeleteButton(item)}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Delete Confirmation Dialog */}
      {deleteItemContext && (
        <DeleteDialog
          description={`Are you sure you want to delete this ${dataTypeName}?`}
          errors={deleteItemContext?.errors}
          isDeleting={deleteItemContext?.isDeleting}
          isOpen={deleteDialogOpen}
          itemName={itemToDelete ? deleteItemContext?.getItemDisplayName?.(itemToDelete) || getItemDisplayName(itemToDelete) || dataTypeName : dataTypeName}
          itemType={dataTypeName}
          title={`Delete ${dataTypeName}`}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </Card>
  );
}
